import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, ListGroup, Badge } from "react-bootstrap";
import axios from "axios";
import config from "../../Common/Configurations/APIConfig";
import { handleSuccess, handleError } from "../../Common/Layout/CustomAlerts";
import moment from "moment";

export default function ServiceTaskComments({
  serviceTaskId,
  onClose,
  headerconfig,
}) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const commentsEndRef = useRef(null);
  const commentsContainerRef = useRef(null);

  useEffect(() => {
    if (serviceTaskId) {
      GetComments();
    }
  }, [serviceTaskId]);

  useEffect(() => {
    if (commentsEndRef.current && !isFetching) {
      scrollToBottom();
    }
  }, [comments]);

  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const GetComments = () => {
    setIsLoading(true);
    axios
      .get(
        config.APIACTIVATEURL + config.GETCOMMENTS + "/" + serviceTaskId,
        headerconfig
      )
      .then((res) => {
        if (res.data.statusCode === 200) {
          const sortedComments = res.data.data.sort(
            (a, b) => new Date(a.createdDate) - new Date(b.createdDate)
          );
          setComments(sortedComments);
        } else {
          handleError(res.data.message);
        }
      })
      .catch((error) => {
        handleError("Error fetching comments");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddComment = () => {
    if (!newComment.trim()) {
      handleError("Comment text cannot be empty");
      return;
    }

    setIsLoading(true);
    axios
      .post(
        config.APIACTIVATEURL + config.CREATECOMMENT,

        {
          serviceTaskId,
          commentText: newComment,
        },
        headerconfig
      )
      .then((res) => {
        if (res.data.statusCode === 200) {
          handleSuccess("Comment added successfully");
          setNewComment("");
          if (res.data.data) {
            setComments((prev) => [...prev, res.data.data]);
          } else {
            GetComments();
          }
        } else {
          handleError(res.data.message);
        }
      })
      .catch((error) => {
        handleError("Error adding comment");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Task Comments</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div
          ref={commentsContainerRef}
          style={{
            height: "400px",
            overflowY: "auto",
            padding: "20px",
            borderBottom: "1px solid #dee2e6",
          }}
        >
          {isLoading && !comments.length ? (
            <div className="text-center">Loading comments...</div>
          ) : comments.length === 0 ? (
            <div className="text-muted text-center">No comments yet</div>
          ) : (
            <ListGroup variant="flush">
              {comments.map((comment) => (
                <ListGroup.Item
                  key={comment.commentId}
                  className="border-0 px-0"
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <Badge bg="light" text="dark">
                      {moment(comment.createdDate).format("MMM D, YYYY h:mm A")}
                    </Badge>
                  </div>
                  <div className="comment-text bg-light p-3 rounded">
                    {comment.commentText}
                  </div>
                </ListGroup.Item>
              ))}
              <div ref={commentsEndRef} />
            </ListGroup>
          )}
        </div>

        <div className="sticky-bottom bg-white p-3 border-top">
          <Form.Group>
            <Form.Label>Add New Comment</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter your comment here..."
              style={{ resize: "none" }}
            />
            <div className="d-flex justify-content-end mt-2">
              <Button
                variant="primary"
                onClick={handleAddComment}
                disabled={isLoading || !newComment.trim()}
              >
                {isLoading ? "Posting..." : "Post Comment"}
              </Button>
            </div>
          </Form.Group>
        </div>
      </Modal.Body>
    </Modal>
  );
}
